// SPDX-FileCopyrightText: 2026 Angels' Ware
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { AlertCircle, Mail, ShieldCheck } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { AuthLayout } from "../../components/auth/AuthLayout";
import { useAuth } from "../../auth";
import { api } from "../../api";
import { explainApiError } from "../../lib/explainApiError";
import type { InvitationDetails } from "../../types";
import { formatDateTime } from "../../lib/datetime";
import { ErrorNotice } from "../../components/ui/ErrorNotice";
import { ICON } from "../../icons";
import { Notice } from "../../components/ui/Notice";
import { Loading } from "../../components/ui/Loading";

// AcceptInvite is the landing page for an invite link. It's reachable
// without auth so a recipient can read who invited them and to which
// org before deciding to sign in. If they're already signed in (with
// the right email), the Accept button immediately binds the membership
// and routes to the new org. Otherwise we deep-link to /signin (or
// /signup) with the email pre-filled and the invite token preserved so
// the post-signin flow can continue from here.
export function AcceptInvite() {
  const { t } = useTranslation();
  const { token: inviteToken } = useParams();
  const navigate = useNavigate();
  const { token, me, setActiveTenant, signOut } = useAuth();
  const [details, setDetails] = useState<InvitationDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  const load = useCallback(async () => {
    if (!inviteToken) return;
    try {
      const d = await api.viewInvitation(inviteToken);
      setDetails(d);
      setError(null);
    } catch (e) {
      setError(explainApiError(e, t));
    }
  }, [inviteToken, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async () => {
    if (!token || !inviteToken) return;
    setAccepting(true);
    setError(null);
    try {
      const r = await api.acceptInvitation(token, inviteToken);
      setActiveTenant(r.tenant);
      navigate("/");
    } catch (e) {
      setError(explainApiError(e, t));
    } finally {
      setAccepting(false);
    }
  };

  if (!inviteToken) {
    return (
      <AuthLayout>
        <div className="signin">
          <h1>{t("acceptInvite.missingToken")}</h1>
        </div>
      </AuthLayout>
    );
  }
  if (error && !details) {
    return (
      <AuthLayout>
        <div className="signin">
          <h1>{t("acceptInvite.title")}</h1>
          <div className="error">
            <AlertCircle className="icon-inline" size={ICON.sm} /> {error}
          </div>
        </div>
      </AuthLayout>
    );
  }
  if (!details) {
    return (
      <AuthLayout>
        <div className="signin">
          <h1>{t("acceptInvite.title")}</h1>
          <Loading inline />
        </div>
      </AuthLayout>
    );
  }

  const usable = details.pending;
  let statusLine: string | null = null;
  if (details.accepted) statusLine = t("acceptInvite.alreadyAccepted");
  else if (details.revoked) statusLine = t("acceptInvite.revoked");
  else if (details.expired) statusLine = t("acceptInvite.expired");

  const signedInAsOther =
    !!me?.subject && me.subject.toLowerCase() !== details.email.toLowerCase();
  const signedInAsRight = !!me?.subject && !signedInAsOther;

  return (
    <AuthLayout>
      <div className="signin invite-card">
        <div className="invite-card-icon">
          <Mail size={28} />
        </div>
        <h1>{t("acceptInvite.title")}</h1>
        <p>
          <Trans
            i18nKey="acceptInvite.body"
            values={{
              email: details.email,
              org: details.tenant_display || details.tenant,
            }}
            components={[<strong />, <code />]}
          />
        </p>
        <ul className="invite-meta-list">
          <li>
            <ShieldCheck size={ICON.sm} />
            {t("acceptInvite.rolesLine", {
              roles: details.roles.map((r) => r.name).join(", "),
            })}
          </li>
          <li>
            {t("acceptInvite.expiresLine", {
              when: formatDateTime(details.expires_at),
            })}
          </li>
          {details.invited_by && (
            <li>{t("acceptInvite.invitedByLine", { who: details.invited_by })}</li>
          )}
        </ul>

        {statusLine && (
          <Notice>
            {statusLine}
          </Notice>
        )}
        {error && (
          <div className="error">
            <AlertCircle className="icon-inline" size={ICON.sm} /> {error}
          </div>
        )}

        {usable && signedInAsRight && (
          <Button variant="primary" onClick={accept} disabled={accepting}>
            {accepting ? t("acceptInvite.accepting") : t("acceptInvite.accept")}
          </Button>
        )}
        {usable && signedInAsOther && (
          <ErrorNotice>
            <Trans
              i18nKey="acceptInvite.wrongAccount"
              values={{ current: me?.subject, invited: details.email }}
              components={[<strong />, <strong />]}
            />
            {/* Escape hatch: sign out so the recipient can sign in as the
                invited address, instead of being stuck on this message. */}
            <div style={{ marginTop: "var(--space-3)" }}>
              <Button
                variant="primary"
                onClick={() => void signOut()}
              >
                {t("acceptInvite.signOutToSwitch")}
              </Button>
            </div>
          </ErrorNotice>
        )}
        {usable && !me && (
          <div className="invite-cta-row">
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  `/signin?email=${encodeURIComponent(details.email)}&invite=${encodeURIComponent(inviteToken)}`,
                )
              }
            >
              {t("acceptInvite.signInToAccept")}
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(
                  `/signup?email=${encodeURIComponent(details.email)}&invite=${encodeURIComponent(inviteToken)}`,
                )
              }
            >
              {t("acceptInvite.signUpToAccept")}
            </Button>
          </div>
        )}
      </div>
    </AuthLayout>
  );
}
